import { Button, Col, Form, InputGroup, Row } from "react-bootstrap";
import { useMyPlayer } from "../../context/MyPlayerContext";
import {
  EPIC_SLUGS,
  MIN_RUNS_OPTIONS,
  RANGE_OPTIONS,
  SORT_OPTIONS
} from "../../constants/epicLeaderboard";
import type { RangeOption } from "../../constants/epicLeaderboard";
import type { EpicLeaderboardSort } from "../../types/epicLeaderboard";
import { getEpicColor } from "../../utils/epicColors";

type Props = {
  epicSlug: string;
  range: RangeOption;
  sort: EpicLeaderboardSort;
  minRuns: number;
  playerInput: string;
  onEpicChange: (slug: string) => void;
  onRangeChange: (range: RangeOption) => void;
  onSortChange: (sort: EpicLeaderboardSort) => void;
  onMinRunsChange: (minRuns: number) => void;
  onPlayerInputChange: (value: string) => void;
};

const EpicLeaderboardFilters = ({
  epicSlug,
  range,
  sort,
  minRuns,
  playerInput,
  onEpicChange,
  onRangeChange,
  onSortChange,
  onMinRunsChange,
  onPlayerInputChange
}: Props) => {
  const { myPlayer } = useMyPlayer();
  const minRunsOptions = MIN_RUNS_OPTIONS.includes(minRuns)
    ? MIN_RUNS_OPTIONS
    : [...MIN_RUNS_OPTIONS, minRuns].sort((a, b) => a - b);

  return (
    <Form onSubmit={(e) => e.preventDefault()}>
      <Row className="mb-3 text-light">
        <Col>
          <div className="d-flex align-items-center flex-wrap gap-2">
            <span className="me-1">Epic:</span>
            {Object.entries(EPIC_SLUGS).map(([slug, name]) => {
              const color = getEpicColor(name);
              const selected = slug === epicSlug;
              return (
                <Button
                  key={slug}
                  size="sm"
                  className="rounded-pill d-inline-flex align-items-center gap-2"
                  style={{
                    borderColor: color,
                    backgroundColor: selected ? color : "transparent",
                    color: selected ? "#161523" : "#f1f4c6"
                  }}
                  onClick={() => onEpicChange(slug)}
                >
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: selected ? "#161523" : color
                    }}
                  />
                  {name}
                </Button>
              );
            })}
          </div>
        </Col>
      </Row>
      <Row className="align-items-end mb-3 text-light g-3">
        <Col xs={6} md={3}>
          <Form.Group>
            <Form.Label>Date range</Form.Label>
            <Form.Select
              value={range}
              onChange={(e) => onRangeChange(e.target.value as RangeOption)}
            >
              {RANGE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col xs={6} md={3}>
          <Form.Group>
            <Form.Label>Sort by</Form.Label>
            <Form.Select
              value={sort}
              onChange={(e) =>
                onSortChange(e.target.value as EpicLeaderboardSort)
              }
            >
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col xs={6} md={2}>
          <Form.Group>
            <Form.Label>Min runs</Form.Label>
            <Form.Select
              value={minRuns}
              onChange={(e) => onMinRunsChange(Number(e.target.value))}
            >
              {minRunsOptions.map((value) => (
                <option key={value} value={value}>
                  {value}+
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col xs={6} md={4}>
          <Form.Group>
            <Form.Label>Player</Form.Label>
            <InputGroup>
              <Form.Control
                type="text"
                placeholder="Search player"
                value={playerInput}
                onChange={(e) => onPlayerInputChange(e.target.value)}
              />
              {myPlayer && playerInput.trim().toLowerCase() !== myPlayer.toLowerCase() && (
                <Button
                  variant="outline-warning"
                  title="Fill in your saved character"
                  onClick={() => onPlayerInputChange(myPlayer)}
                >
                  ★ {myPlayer}
                </Button>
              )}
              {playerInput && (
                <Button
                  variant="outline-secondary"
                  title="Clear the search"
                  onClick={() => onPlayerInputChange("")}
                >
                  Clear
                </Button>
              )}
            </InputGroup>
          </Form.Group>
        </Col>
      </Row>
    </Form>
  );
};

export default EpicLeaderboardFilters;
